import { UPLOAD_RETRY, uploadRetryDelayMs } from './backoff';
import {
  httpUploadError,
  isRetryableUploadError,
  isUploadSessionGone,
  toUploadError,
  UploadError,
  uploadSizeBucket,
} from './errors';
import { planUploadParts } from './parts';
import type {
  BeginUploadRequest,
  CompletedPart,
  CompleteRequest,
  MultipartRef,
  PartUrlsRequest,
  ResumeToken,
  UploadTicket,
} from './protocol';
import {
  createStallWatchdog,
  DEFAULT_UPLOAD_TIMERS,
  UPLOAD_STALL_MS,
  type UploadTimers,
} from './stall-watchdog';

/**
 * One upload, done the way users expect from any serious app:
 *
 * - every request is retried with backoff on a dropped connection, a stall,
 *   a 5xx / 429 / 408, or an expired signed URL (re-signed first);
 * - a request whose bytes stop moving is cut after the stall window;
 * - big files go up in parts, so a failure costs one part, not the file, and
 *   a failed upload hands back a resume token that continues from the parts
 *   already stored;
 * - where the platform can tell (the phone), no attempt is spent while
 *   offline: the upload waits for the connection and carries on;
 * - the caller's signal cancels everything, including the server session.
 *
 * Platform code supplies only the HTTP: `api` (JSON calls to our server) and
 * `transport` (the PUT to storage, with byte progress).
 */

export interface UploadTransport {
  /**
   * PUT the whole file, or the `[start, end)` byte range of it, to `url`.
   * Resolve with the status for any HTTP answer; reject only when there is
   * none (network error, or `signal` aborted). `etag` is the answer's ETag
   * when the transport can read it (a part's, for `CompleteRequest.parts`).
   */
  put(req: {
    url: string;
    range?: { start: number; end: number };
    contentType?: string;
    signal: AbortSignal;
    /** Bytes of THIS request sent so far. */
    onProgress: (bytesSent: number) => void;
  }): Promise<{ status: number; retryAfter?: string | null; etag?: string | null }>;
}

export interface UploadApi {
  /** A retried begin passes the same `req` object as the attempt it retries. */
  begin(req: BeginUploadRequest): Promise<UploadTicket>;
  partUrls(req: PartUrlsRequest): Promise<Record<number, string>>;
  listUploadedParts(req: MultipartRef): Promise<number[]>;
  complete(req: CompleteRequest): Promise<void>;
  abort(req: MultipartRef): Promise<void>;
}

export interface UploadNetwork {
  isOnline(): boolean;
  /** Resolves once online (immediately if already). Rejects when `signal` aborts. */
  waitUntilOnline(signal: AbortSignal): Promise<void>;
}

export type UploadStatus =
  | { phase: 'uploading' }
  | { phase: 'retrying'; attempt: number; maxAttempts: number; inMs: number }
  | { phase: 'waiting-for-network' };

export interface UploadFileOptions {
  file: { name: string; size: number; contentType: string };
  api: UploadApi;
  transport: UploadTransport;
  signal?: AbortSignal;
  /** Whole-file ratio, 0–1. It can step back when a whole-file PUT restarts. */
  onProgress?: (ratio: number) => void;
  onStatus?: (status: UploadStatus) => void;
  /** The phone passes its connectivity; web leaves it out and retries on a timer. */
  network?: UploadNetwork;
  /** Continue an interrupted multipart upload (from `UploadError.resume`). */
  resume?: ResumeToken;
  stallMs?: number;
  maxAttempts?: number;
  /** Parts in flight at once. */
  concurrency?: number;
  timers?: UploadTimers;
  random?: () => number;
}

export interface UploadFileResult {
  filePath: string;
}

const DEFAULT_CONCURRENCY = 3;

export async function uploadFile(options: UploadFileOptions): Promise<UploadFileResult> {
  const signal = options.signal ?? new AbortController().signal;
  const ctx = makeContext(options, signal);
  try {
    return await runUpload(ctx);
  } catch (thrown) {
    if (thrown instanceof UploadError) {
      thrown.diagnostics ??= {
        sizeBucket: uploadSizeBucket(options.file.size),
        contentType: options.file.contentType,
        retries: ctx.retries,
      };
    }
    throw thrown;
  }
}

async function runUpload(ctx: Ctx): Promise<UploadFileResult> {
  const { options } = ctx;
  options.onStatus?.({ phase: 'uploading' });

  if (options.resume) {
    try {
      return await uploadParts(ctx, options.resume, null);
    } catch (thrown) {
      // The stored session is gone (R2 dropped or aborted it): "Try again"
      // begins a fresh upload instead of failing on the dead one forever.
      if (!isUploadSessionGone(thrown)) throw thrown;
    }
  }

  // One request object for every attempt, so an adapter can tell a retry of
  // this begin from a new upload (`UploadApi.begin`).
  const request: BeginUploadRequest = {
    filename: options.file.name,
    size: options.file.size,
    contentType: options.file.contentType,
  };
  const ticket = await ctx.retry(() => options.api.begin(request));
  if (ticket.mode === 'single') return uploadWhole(ctx, ticket);
  return uploadParts(
    ctx,
    { filePath: ticket.filePath, uploadId: ticket.uploadId, partSize: ticket.partSize },
    ticket.partUrls,
  );
}

// ── internals ──────────────────────────────────────────────────────────────

interface Ctx {
  options: UploadFileOptions;
  signal: AbortSignal;
  /** Failed requests retried so far, across every part. */
  retries: number;
  retry<T>(
    run: (signal: AbortSignal) => Promise<T>,
    onExpired?: () => void | Promise<void>,
    group?: AbortSignal,
  ): Promise<T>;
  /** Resolves with the answer's ETag, or null when the transport has none. */
  put(
    url: string,
    range: { start: number; end: number } | undefined,
    onBytes: (n: number) => void,
    group?: AbortSignal,
  ): Promise<string | null>;
}

function makeContext(options: UploadFileOptions, signal: AbortSignal): Ctx {
  const maxAttempts = options.maxAttempts ?? UPLOAD_RETRY.maxAttempts;
  let retries = 0;
  const timers = options.timers ?? DEFAULT_UPLOAD_TIMERS;

  const sleep = (ms: number, s: AbortSignal) =>
    new Promise<void>((resolve, reject) => {
      if (s.aborted) return reject(new UploadError('cancelled'));
      const handle = timers.setTimer(() => {
        s.removeEventListener('abort', onAbort);
        resolve();
      }, ms);
      const onAbort = () => {
        timers.clearTimer(handle);
        reject(new UploadError('cancelled'));
      };
      s.addEventListener('abort', onAbort, { once: true });
    });

  const awaitNetwork = async (s: AbortSignal) => {
    const network = options.network;
    if (!network || network.isOnline()) return;
    options.onStatus?.({ phase: 'waiting-for-network' });
    try {
      await network.waitUntilOnline(s);
    } catch {
      throw new UploadError('cancelled');
    }
    options.onStatus?.({ phase: 'uploading' });
  };

  const retry: Ctx['retry'] = async (run, onExpired, group) => {
    const s = group ?? signal;
    let failures = 0;
    for (;;) {
      if (s.aborted) throw new UploadError('cancelled');
      await awaitNetwork(s);
      try {
        return await run(s);
      } catch (thrown) {
        const error = s.aborted ? new UploadError('cancelled') : toUploadError(thrown);
        if (error.kind === 'cancelled' || !isRetryableUploadError(error)) throw error;
        // A drop while the phone knows it is offline costs no attempt: it waits.
        if (options.network && !options.network.isOnline()) continue;
        failures += 1;
        if (failures >= maxAttempts) throw error;
        retries += 1;
        if (error.kind === 'expired') await onExpired?.();
        const inMs = uploadRetryDelayMs(failures, {
          random: options.random,
          retryAfterMs: error.retryAfterMs,
        });
        options.onStatus?.({
          phase: 'retrying',
          attempt: failures + 1,
          maxAttempts,
          inMs,
        });
        await sleep(inMs, s);
        options.onStatus?.({ phase: 'uploading' });
      }
    }
  };

  const put: Ctx['put'] = async (url, range, onBytes, group) => {
    const parent = group ?? signal;
    // A cancel can land while the part URL was being fetched: an 'abort'
    // listener added after the fact never fires, so check first.
    if (parent.aborted) throw new UploadError('cancelled');
    const request = new AbortController();
    let stalled = false;
    const forward = () => request.abort();
    parent.addEventListener('abort', forward, { once: true });
    const watchdog = createStallWatchdog({
      stallMs: options.stallMs ?? UPLOAD_STALL_MS,
      timers,
      onStall: () => {
        stalled = true;
        request.abort();
      },
    });
    try {
      const res = await options.transport.put({
        url,
        range,
        contentType: options.file.contentType,
        signal: request.signal,
        onProgress: (n) => {
          watchdog.kick();
          onBytes(n);
        },
      });
      if (stalled) throw new UploadError('stalled', 'No bytes moved');
      if (res.status < 200 || res.status >= 300) {
        throw httpUploadError(res.status, res.retryAfter, 'storage');
      }
      return res.etag || null;
    } catch (thrown) {
      if (parent.aborted) throw new UploadError('cancelled');
      if (stalled) throw new UploadError('stalled', 'No bytes moved');
      throw toUploadError(thrown);
    } finally {
      watchdog.stop();
      parent.removeEventListener('abort', forward);
    }
  };

  return {
    options,
    signal,
    get retries() {
      return retries;
    },
    retry,
    put,
  };
}

async function uploadWhole(
  ctx: Ctx,
  first: Extract<UploadTicket, { mode: 'single' }>,
): Promise<UploadFileResult> {
  const { file, api, onProgress } = ctx.options;
  let ticket = first;
  await ctx.retry(
    () =>
      ctx.put(ticket.signedUrl, undefined, (sent) => {
        if (file.size > 0) onProgress?.(Math.min(1, sent / file.size));
      }),
    async () => {
      // Re-sign as ONE PUT: without a size the server never opens a multipart
      // session (the size was checked when the upload began). A fresh ticket
      // may carry a new path; the finished upload uses it.
      const next = await ctx.retry(() =>
        api.begin({ filename: file.name, contentType: file.contentType }),
      );
      if (next.mode === 'single') ticket = next;
      else
        await api
          .abort({ filePath: next.filePath, uploadId: next.uploadId })
          .catch(() => undefined);
    },
  );
  onProgress?.(1);
  return { filePath: ticket.filePath };
}

async function uploadParts(
  ctx: Ctx,
  token: ResumeToken,
  initialUrls: Record<number, string> | null,
): Promise<UploadFileResult> {
  const { file, api, onProgress } = ctx.options;
  const urls: Record<number, string> = { ...(initialUrls ?? {}) };
  const done = new Set<number>(
    initialUrls ? [] : await ctx.retry(() => api.listUploadedParts(token)),
  );

  const parts = planUploadParts(file.size, token.partSize);
  // Each part's ETag as its PUT answered, for storage that cannot list them.
  const etags = new Map<number, string>();
  let bytesDone = 0;
  for (const p of parts) if (done.has(p.partNumber)) bytesDone += p.end - p.start;
  const inFlight = new Map<number, number>();
  const report = () => {
    let sum = bytesDone;
    for (const n of inFlight.values()) sum += n;
    if (file.size > 0) onProgress?.(Math.min(1, sum / file.size));
  };
  report();

  // One failed part stops the others; the stored parts stay for a resume.
  const group = new AbortController();
  const forward = () => group.abort();
  ctx.signal.addEventListener('abort', forward, { once: true });
  // Cancelled during begin / listing: the listener above will never fire.
  if (ctx.signal.aborted) group.abort();
  let failure: UploadError | null = null;

  const urlFor = async (partNumber: number) => {
    if (!urls[partNumber]) {
      const fresh = await ctx.retry(
        () => api.partUrls({ ...token, partNumbers: [partNumber] }),
        undefined,
        group.signal,
      );
      Object.assign(urls, fresh);
    }
    return urls[partNumber];
  };

  const queue = parts.filter((p) => !done.has(p.partNumber));
  const worker = async () => {
    for (let part = queue.shift(); part && !failure; part = queue.shift()) {
      const current = part;
      try {
        await ctx.retry(
          async () => {
            try {
              const etag = await ctx.put(
                await urlFor(current.partNumber),
                { start: current.start, end: current.end },
                (sent) => {
                  inFlight.set(current.partNumber, sent);
                  report();
                },
                group.signal,
              );
              if (etag) etags.set(current.partNumber, etag);
            } finally {
              inFlight.delete(current.partNumber);
            }
          },
          () => {
            delete urls[current.partNumber];
          },
          group.signal,
        );
        bytesDone += current.end - current.start;
        report();
      } catch (thrown) {
        failure ??= toUploadError(thrown);
        group.abort();
      }
    }
  };

  try {
    const concurrency = Math.max(1, ctx.options.concurrency ?? DEFAULT_CONCURRENCY);
    await Promise.all(
      Array.from({ length: Math.min(concurrency, queue.length || 1) }, worker),
    );
  } finally {
    ctx.signal.removeEventListener('abort', forward);
  }

  if (ctx.signal.aborted) {
    // The user cancelled: drop the stored parts too.
    await api.abort(token).catch(() => undefined);
    throw new UploadError('cancelled');
  }
  if (failure) {
    const error: UploadError = failure;
    if (canResume(error)) error.resume = token;
    throw error;
  }

  // The part list goes along only when every part's ETag was read in THIS
  // run; a resumed upload's earlier parts have none, and our server lists them.
  const tagged: CompletedPart[] = parts.flatMap((p) => {
    const etag = etags.get(p.partNumber);
    return etag ? [{ partNumber: p.partNumber, etag }] : [];
  });
  const finish: CompleteRequest =
    tagged.length === parts.length ? { ...token, parts: tagged } : token;
  try {
    await ctx.retry(() => api.complete(finish));
  } catch (thrown) {
    const error = toUploadError(thrown);
    if (canResume(error)) error.resume = token;
    throw error;
  }
  onProgress?.(1);
  return { filePath: token.filePath };
}

/**
 * A resume token is only worth handing back while the session can still be
 * continued: not after a cancel, a refused size (the server aborts it), a
 * session that no longer exists, or a failure that continuing would repeat.
 */
const canResume = (error: UploadError) =>
  error.resumable &&
  error.kind !== 'cancelled' &&
  error.kind !== 'too-large' &&
  !isUploadSessionGone(error);
